import axios from 'axios';
import type { Dispatch } from '@reduxjs/toolkit';
import { UserControllerApi } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import {
  setLocationPermissions,
  clearLocationPermissions,
  setAccountPermissions,
  clearAccountPermissions
} from '../store/permissions/permissionsSlice';
import type { Permission } from '../constants/permissions';

// Reading what the signed-in user may do. Kept out of the hooks because it is
// needed in two situations, not one: on mount, and again after a write that may
// have changed the caller's own permissions.
//
// Both functions resolve either way — a failure is reported by clearing the
// scope, which denies every gate in it, so no caller has to handle a rejection
// to stay safe.

const createUserApi = () => new UserControllerApi(createApiConfiguration());

/**
 * Re-reads the account-wide permissions into the store.
 *
 * The `accountAdmin` flag on the response is ignored on purpose: the backend has
 * already folded what it grants into `permissions`, so storing it would give
 * gates a second, looser way to ask the same question.
 */
export const fetchAccountPermissions = async (
  dispatch: Dispatch,
  signal?: AbortSignal
): Promise<void> => {
  try {
    const response = await createUserApi().getCurrentUserAccountPermissions({ signal });
    dispatch(
      setAccountPermissions({
        permissions: Array.from<Permission>(response.data.permissions)
      })
    );
  } catch (e: unknown) {
    if (axios.isCancel(e)) return;
    dispatch(clearAccountPermissions());
  }
};

/**
 * Re-reads one location's permissions into the store.
 *
 * The backend returns an empty set rather than a 404 when the user holds no role
 * there, so "may do nothing here" arrives as data and only a real transport
 * failure lands in the catch.
 */
export const fetchLocationPermissions = async (
  dispatch: Dispatch,
  locationId: string,
  signal?: AbortSignal
): Promise<void> => {
  try {
    const response = await createUserApi().getCurrentUserPermissions({ locationId }, { signal });
    dispatch(
      setLocationPermissions({
        // The response's own locationId, not the argument: it is what the
        // backend actually answered for.
        locationId: response.data.locationId,
        permissions: Array.from<Permission>(response.data.permissions)
      })
    );
  } catch (e: unknown) {
    if (axios.isCancel(e)) return;
    dispatch(clearLocationPermissions());
  }
};
